import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart2, Globe, Link as LinkIcon, PuzzleIcon } from "lucide-react";
import InsightsTab from "@/components/spy-dashboard/InsightsTab";
import DomainsTab from "@/components/spy-dashboard/DomainsTab";
import URLsTab from "@/components/spy-dashboard/URLsTab";
import SegmentsTab from "@/components/spy-dashboard/SegmentsTab";
import {
  SimpleDateRange,
  type SimpleDateRangeOption,
} from "@/components/spy-dashboard/SimpleDateRange";

export default function SpySerpDashboard() {
  const [activeTab, setActiveTab] = useState("domains");
  const [searchParams, setSearchParams] = useSearchParams();
  const dateRange = (searchParams.get("dateRange") ||
    "last_7_days") as SimpleDateRangeOption;
  const customFrom = searchParams.get("from") || undefined;
  const customTo = searchParams.get("to") || undefined;

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setSearchParams((prev) => {
      prev.set("page", "1");
      prev.delete("sort");
      prev.delete("desc");
      return prev;
    });
  };

  const handleParamChange = (key: string, value: string) => {
    setSearchParams((prev) => {
      prev.set(key, value);
      prev.set("page", "1");
      return prev;
    });
  };

  return (
    <div className="flex flex-col h-full w-full bg-background overflow-hidden space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            SpySerp Dashboard
          </h1>
          <p className="text-muted-foreground flex items-center gap-2 mt-1">
            SpySerp Data
          </p>
        </div>

        <div className="flex items-center gap-3">
          <SimpleDateRange
            dateRange={dateRange}
            onDateRangeChange={(v) => handleParamChange("dateRange", v)}
            customFrom={customFrom}
            customTo={customTo}
            onCustomDateChange={(type, date) => handleParamChange(type, date)}
          />
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="flex-1 flex flex-col min-h-0"
      >
        <TabsList className="w-fit">
          <TabsTrigger
            value="insights"
            className="flex items-center gap-2 cursor-pointer"
          >
            <BarChart2 className="w-4 h-4" />
            Insights
          </TabsTrigger>
          <TabsTrigger
            value="domains"
            className="flex items-center gap-2 cursor-pointer"
          >
            <Globe className="w-4 h-4" />
            Domains
          </TabsTrigger>
          <TabsTrigger
            value="urls"
            className="flex items-center gap-2 cursor-pointer"
          >
            <LinkIcon className="w-4 h-4" />
            URLs
          </TabsTrigger>
          <TabsTrigger
            value="segments"
            className="flex items-center gap-2 cursor-pointer"
          >
            <PuzzleIcon className="w-4 h-4" />
            Segments
          </TabsTrigger>
        </TabsList>

        <div className="flex-1 mt-6 overflow-y-auto">
          <TabsContent value="insights" className="h-full m-0">
            <InsightsTab />
          </TabsContent>

          <TabsContent value="domains" className="h-full m-0">
            <DomainsTab />
          </TabsContent>

          <TabsContent value="urls" className="h-full m-0">
            <URLsTab />
          </TabsContent>

          <TabsContent value="segments" className="h-full m-0">
            <SegmentsTab />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
